import { useInvalidate } from '@refinedev/core'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

export function useUserActions() {
  const invalidate = useInvalidate()
  const queryClient = useQueryClient()

  const refresh = () => {
    void invalidate({ resource: 'users', invalidates: ['list'] })
    invalidateAdminCaches(queryClient)
  }

  const grantBalance = async (
    userId: string,
    amount: string,
    description?: string,
  ) => {
    const result = await client.POST(
      '/v1/admin/users/{user_id}/balances/grants',
      {
        params: { path: { user_id: userId } },
        body: { amount, description },
      },
    )
    unwrapApiData(result, 'Failed to grant balance')
    void message.success('Balance granted successfully')
    refresh()
  }

  const voidGrant = async (userId: string, grantId: string) => {
    const result = await client.POST(
      '/v1/admin/users/{user_id}/balances/grants/{grant_id}/void',
      {
        params: { path: { user_id: userId, grant_id: grantId } },
      },
    )
    unwrapApiData(result, 'Failed to revoke grant')
    void message.success('Grant revoked')
    refresh()
  }

  const chargeBalance = async (
    userId: string,
    cost: string,
    reason: string,
    description?: string,
  ) => {
    const result = await client.POST(
      '/v1/admin/users/{user_id}/balances/charge',
      {
        params: { path: { user_id: userId } },
        body: { cost, reason, description },
      },
    )
    unwrapApiData(result, 'Failed to charge balance')
    void message.success('Balance charged successfully')
    refresh()
  }

  const setRole = async (roleName: string, ids: string[]) => {
    const result = await client.POST('/v1/admin/users/role', {
      body: { roleName, ids },
    })
    unwrapApiData(result, 'Failed to set role')
    void message.success('Role updated')
    refresh()
  }

  const setGroup = async (groupName: string, ids: string[]) => {
    const result = await client.POST('/v1/admin/users/group', {
      body: { groupName, ids },
    })
    unwrapApiData(result, 'Failed to set group')
    void message.success('Group updated')
    refresh()
  }

  return {
    grantBalance,
    voidGrant,
    chargeBalance,
    setRole,
    setGroup,
  }
}
